import cl from 'classnames';
import * as React from 'react';
import { DragSourceMonitor, useDrag } from 'react-dnd';
import styled from 'styled-components';

import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { scrollBarStylesGeneral } from '@doltech/utils/lib/css-style';

const Main = styled.div`
  padding: 16px 0;
  .header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding: 0 14px;
    &.movable {
      cursor: move;
    }
  }

  .content {
    .list-item {
      display: flex;
      align-items: center;
      box-sizing: border-box;
      border-radius: 16px;
      padding: 4px;

      margin-bottom: 4px;

      cursor: move;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .floating-list-container {
    overflow-y: auto;
    max-height: 320px;
    ${scrollBarStylesGeneral()}
  }
`;

export interface DragItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDragEnd'> {
  accept?: string;
  children: React.ReactElement | string;

  dragData: any;
  onDragEnd?: (
    draggedItem: { dragData: any; type: string; index?: number },
    monitor: DragSourceMonitor<any>
  ) => void;
  onDropEnd?: (
    droppedItem: { dragData: any; type: string; index?: number },
    monitor?: DragSourceMonitor<any>
  ) => void;
  index?: number;
  isDropped?: boolean;
  /* Show preview when dragging or not */
  preview?: boolean;
}

const DragItemWrapper = styled.div<{ isDragging?: boolean; isDropped?: boolean }>`
  position: relative;
  display: flex;
  border: 1px solid transparent;
  align-items: center;
  box-sizing: border-box;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: move;
  overflow: hidden;
  padding: ${(p) => (p.isDragging ? 0 : 4)}px;
  font-weight: 400;

  &:not(.preview) {
    opacity: ${(p) => (p.isDragging ? 0 : 1)};
  }

  &:last-child {
    margin-bottom: 0;
  }
  &.hover,
  &:hover {
    border: 1px solid ${colorsV2.gray20};
    box-sizing: border-box;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
    background-color: ${colorsV2.white100};
    opacity: 0.99;
    border-radius: 16px;
  }
`;

const DragItemPreview = styled.div<{ isDragging?: boolean; isDropped?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: -1;
  opacity: ${(p) => (p.isDragging ? 1 : 0)};
  background-color: ${colorsV2.white100};
  border-radius: 16px;
  border: 1px dashed ${colorsV2.gray20};
  transform: translate(0, 0);
  padding: 4px;
`;

const DragItemClone = styled.div<{ isDragging?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
  visibility: ${(p) => (p.isDragging ? 'visible' : 'hidden')};
  background-color: ${colorsV2.blue5};
  padding: 4px;
  .right {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  }
`;

export const getEmptyImage = (): HTMLImageElement => {
  const emptyImage = document.createElement('img');
  emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

  return emptyImage;
};

export const DragItem = (props: DragItemProps) => {
  const { accept, children, dragData, onDragEnd, preview: isShowPreview = true, ...rest } = props;
  const [{ isDragging }, drag, preview] = useDrag({
    type: accept,
    item: () => ({ dragData }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    previewOptions: {
      captureDraggingState: true,
    },
    end: onDragEnd,
  });

  React.useEffect(() => {
    if (!isShowPreview) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [isShowPreview, preview]);

  return (
    <DragItemWrapper ref={drag} isDragging={isDragging} className="list-item" {...rest}>
      {isShowPreview ? (
        <>
          <DragItemClone ref={preview} isDragging={isDragging}>
            {children}
          </DragItemClone>
          <DragItemPreview isDragging={isDragging} ref={preview}>
            {children}
          </DragItemPreview>
        </>
      ) : null}
      {children}
    </DragItemWrapper>
  );
};

interface FloatingDraggableBoxProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  movable?: boolean;
}

interface FloatingListPaginationProps {
  items: any;
}

const Item = styled.div`
  &:not(:last-child) {
    margin-bottom: 8px;
    border-radius: 16px;
  }
`;
const OptionsDiv = styled.div`
  padding: 0 14px;
  &::-webkit-scrollbar {
    width: 11px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${colorsV2.black5};
    border-radius: 20px;
    border-left: 4px solid ${colorsV2.white100};
    border-right: 4px solid ${colorsV2.white100};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${colorsV2.black80};
    border-radius: 20px;
    border-left: 4px solid ${colorsV2.white100};
    border-right: 4px solid ${colorsV2.white100};
  }
  &:hover {
    overflow-y: auto;
  }
`;
export const FloatingListPagination = React.memo(({ items }: FloatingListPaginationProps) => {
  return (
    <div className="floating-list-container">
      <OptionsDiv>
        {items.map((item, index) => (
          <Item key={index}>{item}</Item>
        ))}
      </OptionsDiv>
    </div>
  );
});

export const FloatingDraggableBox = React.forwardRef(
  (props: FloatingDraggableBoxProps, ref: any) => {
    const { children, title, movable = true } = props;

    return (
      <Main className="floating-draggable-box" ref={ref}>
        <div className={cl('header', { movable })}>
          <Typography.Text
            variant="semi-bold/16-28"
            className="account-row"
            style={{ marginLeft: 8 }}
          >
            {title}
          </Typography.Text>
        </div>
        <div className="content">
          <FloatingListPagination items={children} />
        </div>
      </Main>
    );
  }
);
